import moment from 'moment'
import { EhrAppointmentStateKind, InsurancePolicy } from '@src/types'
import { DEFAULT_VIDEO_CALL_INFO, VIDEO_CALL_PAST_APPOINTMENT_BUFFER_MINUTES } from './constants'
import { EhrAppointmentsQuery } from './graphql/ehrAppointments.generated'
import { LatestUserEhrAppointmentQuery } from './graphql/fetchAppointmentInfo.generated'

export type EhrAppointmentCustomType = EhrAppointmentsQuery['ehrAppointments']['appointments'][0]
export type LatestEhrAppointmentCustomType = LatestUserEhrAppointmentQuery['latestUserEhrAppointment']

export type AppointmentDetails = EhrAppointmentCustomType & {
  title: string
  isSchedulingPending: boolean
  isCallConfirmed: boolean
  isCallInReview: boolean
  isCallCancelled: boolean
  isCallOccurred: boolean
}

export const getAppointmentDetails = (
  appointment: EhrAppointmentCustomType,
): AppointmentDetails => {
  const lastState = appointment.lastState

  return {
    ...appointment,
    title: appointment.title || DEFAULT_VIDEO_CALL_INFO.title,
    isSchedulingPending: lastState.kind === EhrAppointmentStateKind.PendingSchedule,
    isCallConfirmed: lastState.kind === EhrAppointmentStateKind.Scheduled,
    isCallInReview: lastState.kind === EhrAppointmentStateKind.InReview,
    isCallCancelled: lastState.kind === EhrAppointmentStateKind.Cancelled,
    isCallOccurred: lastState.kind === EhrAppointmentStateKind.Occurred,
  }
}

export const isUnusedCallsInfoAvailable = (insurancePolicy: InsurancePolicy | null): boolean => {
  if (!insurancePolicy) {
    return false
  }

  return insurancePolicy.unitsLeft !== null && insurancePolicy.unitsLeft !== undefined
}

export const insuranceSubmittedWithoutCall = (
  insurancePolicy: InsurancePolicy | null,
  appointmentDetails: AppointmentDetails | undefined,
): boolean => {
  return !!insurancePolicy && !appointmentDetails?.id && !insurancePolicy.declined
}

export const isAppointmentScheduled = (appointmentDetails: AppointmentDetails): boolean =>
  appointmentDetails.isCallConfirmed || appointmentDetails.isCallInReview

export const isAppointmentPastAccessWindow = (meetingAt: string): boolean =>
  moment(meetingAt).add(VIDEO_CALL_PAST_APPOINTMENT_BUFFER_MINUTES, 'minutes').isBefore(moment())

export const isAppointmentCancelled = (
  appointment?: Pick<EhrAppointmentCustomType, 'lastState'> | null,
): boolean => {
  const stateKind = appointment?.lastState.kind

  return [EhrAppointmentStateKind.Cancelled, EhrAppointmentStateKind.LateCancellation].includes(
    stateKind as EhrAppointmentStateKind,
  )
}

export const isAppointmentRescheduled = (
  appointment:
    | (Pick<EhrAppointmentCustomType, 'lastState' | 'meetingAt'> & { meetingAt?: string })
    | null
    | undefined,
  selectedSlotTime: string,
): boolean => {
  const stateKind = appointment?.lastState.kind

  return (
    appointment?.meetingAt === selectedSlotTime ||
    [
      EhrAppointmentStateKind.PendingReschedule,
      EhrAppointmentStateKind.Rescheduled,
      EhrAppointmentStateKind.LateReschedule,
    ].includes(stateKind as EhrAppointmentStateKind)
  )
}
